import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { QueryParams } from 'src/components/interfaces';
import { RowClickedEvent } from 'ag-grid-community';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { getRepaymentHistory } from '../../actions/RepaymentHistoryAction';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { All_CASES_COLUMN_DEFS } from './repaymentColDefs';
import {
  REPAYMENTS_TABLE,
  defaultRepaymentsTabPageRequest,
  PAGE_NO,
  PAGE_SIZE,
  ACTIVE_ROW_INDEX
} from './const';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import styles from './index.module.scss';
import { Button, Drawer, Typography } from '@navi/web-ui/lib/primitives';
import { RowHeightParams } from 'ag-grid-community/dist/lib/main';
import AllBankDetailsDrawer from './AllBankDetailsDrawer';
import AdditionalDetails from './AdditionalDetails';
import React from 'react';
import { assignIndexToRepaymentList } from './utils';
import { setRepaymentHistory } from '../../reducers/caseDetailSlice';
import { STORE_KEYS } from '../../constants';

const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);

const RepaymentTab = () => {
  const { loanId = '', customerId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { [REPAYMENTS_TABLE]: queryParams } = readQueryParams();
  const repaymentData = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.repaymentsHistory
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleToggleDrawer = () => {
    setIsDrawerOpen(prev => !prev);
  };

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Repayments_PageLand);
  }, []);

  useEffect(() => {
    const anythingInParams = queryParams && Object.keys(queryParams).length !== 0;
    if (loanId && customerId) {
      dispatch(
        getRepaymentHistory(
          loanId,
          customerId,
          anythingInParams ? queryParams : defaultRepaymentsTabPageRequest
        )
      );
    }
  }, [searchParams]);

  const updateQueryParamsAndNavigate = (params?: QueryParams, lastUsed?: string) => {
    let url = '';
    if (queryParams && params) {
      url = createQueryParams({ [REPAYMENTS_TABLE]: { ...queryParams, ...params, lastUsed } });
    } else {
      url = createQueryParams({
        [REPAYMENTS_TABLE]: { ...defaultRepaymentsTabPageRequest, ...params, lastUsed }
      });
    }
    navigate(url, { replace: true });
  };

  const handlePageChange = (page: number) => {
    updateQueryParamsAndNavigate({ [PAGE_NO]: (page - 1).toString() });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Repayments_PaginationClick);
  };

  const getCurrentPageNumber = (): number => {
    return Number(queryParams?.page) + 1 || 1;
  };

  const handlePageSizeChange = (size: number) =>
    updateQueryParamsAndNavigate({
      [PAGE_SIZE]: size.toString(),
      [PAGE_NO]: defaultRepaymentsTabPageRequest.page
    });

  const getCurrentPageSize = (): number => {
    return Number(queryParams?.size) || defaultRepaymentsTabPageRequest.size;
  };
  const handleRowClick = (e: RowClickedEvent<any>) => {
    // disable click on detailed rows or when required data is not present
    // or if the row is already a detail row
    if (e.data?.isDetailRow || !e.data?.repaymentMetadata) {
      return;
    }

    // Clone data with proper type casting and retain the original structure
    const data = [...(repaymentData?.data?.content || [])];
    const clickedData = e.data;

    // Find clicked index using the specific ID field
    let clickedIndex = data.findIndex(
      item => clickedData.paymentReferenceId === item.paymentReferenceId
    );

    let newData;
    // If next row is detail row, remove it
    if (data[clickedIndex + 1]?.isDetailRow) {
      data.splice(clickedIndex + 1, 1);
      data[clickedIndex] = { ...data[clickedIndex], toggleState: false };
      newData = assignIndexToRepaymentList(data);

      // Maintain the original data structure when dispatching
      const resDataCopy = {
        ...repaymentData?.data,
        content: newData
      };

      dispatch(
        setRepaymentHistory({
          lan: loanId,
          customerRefrenceId: customerId,
          key: STORE_KEYS.REPAYMENTS_HISTORY,
          data: resDataCopy
        })
      );
      return;
    }

    // find detail row index
    const detailRowIndex = data.findIndex(item => {
      return item?.isDetailRow;
    });

    // If detail row exists, remove it
    if (detailRowIndex !== ACTIVE_ROW_INDEX) {
      data[detailRowIndex - 1] = { ...data[detailRowIndex - 1], toggleState: false };
      data.splice(detailRowIndex, 1);
      if (detailRowIndex < clickedIndex) {
        clickedIndex -= 1;
      }
    }

    // New detail row to be appended after the clicked row
    const newRow = {
      ...clickedData,
      isDetailRow: true,
      rowIndex: clickedIndex + 1
    };

    // Insert new detail row and update toggle state
    data.splice(clickedIndex + 1, 0, newRow);
    data[clickedIndex] = { ...data[clickedIndex], toggleState: true };

    // Process the data with the specific helper function
    newData = assignIndexToRepaymentList(data);
    // Maintain the original data structure when dispatching
    const resDataCopy = {
      ...repaymentData?.data,
      content: newData
    };

    dispatch(
      setRepaymentHistory({
        lan: loanId,
        customerRefrenceId: customerId,
        key: STORE_KEYS.REPAYMENTS_HISTORY,
        data: resDataCopy
      })
    );
  };

  const getRowHeight = (params: RowHeightParams) => {
    return params?.data?.isDetailRow ? 100 : 60;
  };

  return (
    <div className={styles.tabContentScroll}>
      <div className={'p-6'}>
        <div className={'mb-4 flex justify-between'}>
          <Typography variant="h3" color={'var(--navi-color-gray-c2)'}>
            Repayment details
          </Typography>

          <Button variant="text" onClick={handleToggleDrawer}>
            View all bank details
          </Button>
          <Drawer
            open={isDrawerOpen}
            onClose={handleToggleDrawer}
            showHeaderDivider={true}
            headerText="All bank details"
          >
            <AllBankDetailsDrawer />
          </Drawer>
        </div>
        <div className={'w-full'} id="repaymentTable">
          <div className={styles.table}>
            <AgTable
              className={'customAgTableStyles'}
              defaultColDef={{
                suppressMovable: true,
                autoHeaderHeight: true,
                wrapHeaderText: true
              }}
              gridOptions={{
                suppressCellSelection: true,
                suppressCellFocus: true,
                suppressScrollOnNewData: true
              }}
              animateRows={true}
              sizeColumnsToFit={true}
              rowData={repaymentData?.data?.content}
              columnDefs={All_CASES_COLUMN_DEFS}
              theme={'alpine'}
              domLayout="autoHeight"
              paginationWrapperClasses={'paginationWrapperClasses'}
              onRowClicked={handleRowClick}
              getRowHeight={getRowHeight}
              suppressCellFocus={true}
              isFullWidthRow={params => !!params?.rowNode?.data?.isDetailRow}
              fullwidthCellRenderer={({ node }: { node: any }) => (
                <AdditionalDetails rowData={node?.data} />
              )}
              rowClassRules={{ 'rr-block': () => true }}
              PaginationComponent={
                <Pagination
                  totalCount={repaymentData?.data?.totalElements || 0}
                  onPageChange={handlePageChange}
                  currentPage={getCurrentPageNumber()}
                  onPageSizeChange={handlePageSizeChange}
                  pageSize={getCurrentPageSize()}
                  pageNumberDropDownPosition={DropDownPosition.TOP}
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepaymentTab;
